import 'core-js/modules/es.promise.js';
import { HttpClient, HttpMethod, HttpStatusCode } from '@hub-la/fe-core-http-client';
import { injectable, inject, Container } from 'inversify';
import { useQuery } from 'react-query';
import 'core-js/modules/es.object.assign.js';
import 'react/jsx-runtime';
import { useContext, createContext } from 'react';
import { Auth } from '@hub-la/fe-core-auth';
import { FirebaseAuth } from '@hub-la/fe-vendors-firebase';
import { AxiosHttpClient } from '@hub-la/fe-vendors-axios';

var BankAccountType;
(function (BankAccountType) {
  BankAccountType[BankAccountType["UNSPECIFIED"] = 0] = "UNSPECIFIED";
  BankAccountType[BankAccountType["TYPE_CHECKING"] = 1] = "TYPE_CHECKING";
  BankAccountType[BankAccountType["TYPE_SAVING"] = 2] = "TYPE_SAVING";
})(BankAccountType || (BankAccountType = {}));
var DocumentType;
(function (DocumentType) {
  DocumentType[DocumentType["UNSPECIFIED"] = 0] = "UNSPECIFIED";
  DocumentType[DocumentType["CNPJ"] = 1] = "CNPJ";
  DocumentType[DocumentType["CPF"] = 2] = "CPF";
  DocumentType[DocumentType["PASSPORT"] = 3] = "PASSPORT";
  DocumentType[DocumentType["ABSENT"] = 100] = "ABSENT";
})(DocumentType || (DocumentType = {}));
var VerificationStatus;
(function (VerificationStatus) {
  VerificationStatus[VerificationStatus["UNSPECIFIED"] = 0] = "UNSPECIFIED";
  VerificationStatus[VerificationStatus["APPROVED"] = 1] = "APPROVED";
  VerificationStatus[VerificationStatus["REJECTED"] = 2] = "REJECTED";
  VerificationStatus[VerificationStatus["PENDING"] = 3] = "PENDING";
})(VerificationStatus || (VerificationStatus = {}));

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __param(paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

let GetUser = class GetUser {
  constructor(httpClient) {
    this.httpClient = httpClient;
  }
  execute() {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
      const URI = (_a = process.env['NX_BFF_WEB_URL']) !== null && _a !== void 0 ? _a : process.env['REACT_APP_BFF_URL'];
      if (!URI) {
        throw new Error('errors.general');
      }
      const response = yield this.httpClient.request({
        url: `${URI}/user/me/reference`,
        method: HttpMethod.GET
      });
      switch (response.statusCode) {
        case HttpStatusCode.OK:
          return response.data;
        default:
          throw new Error('errors.general');
      }
    });
  }
};
GetUser = __decorate([injectable(), __param(0, inject(HttpClient)), __metadata("design:paramtypes", [HttpClient])], GetUser);

let GetUserMock = class GetUserMock extends GetUser {
  execute() {
    return __awaiter(this, void 0, void 0, function* () {
      return Promise.resolve({
        id: '',
        name: '',
        email: '',
        phoneNumber: '',
        picture: ''
      });
    });
  }
};
GetUserMock = __decorate([injectable()], GetUserMock);

const container = new Container();
container.bind(Auth).to(FirebaseAuth);
container.bind(HttpClient).to(AxiosHttpClient);
const ContainerContext = /*#__PURE__*/createContext(container);
const useContainer = () => {
  return useContext(ContainerContext);
};

let GetBusiness = class GetBusiness {
  constructor(httpClient) {
    this.httpClient = httpClient;
  }
  execute() {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
      const URI = (_a = process.env['NX_BFF_WEB_URL']) !== null && _a !== void 0 ? _a : process.env['REACT_APP_BFF_URL'];
      if (!URI) {
        throw new Error('errors.general');
      }
      const response = yield this.httpClient.request({
        url: `${URI}/business`,
        method: HttpMethod.GET
      });
      switch (response.statusCode) {
        case HttpStatusCode.OK:
          return response.data;
        default:
          throw new Error('errors.general');
      }
    });
  }
};
GetBusiness = __decorate([injectable(), __param(0, inject(HttpClient)), __metadata("design:paramtypes", [HttpClient])], GetBusiness);

const useGetBusiness = () => {
  const container = useContainer();
  return useQuery('getBusiness', () => new GetBusiness(container.get(HttpClient)).execute(), {
    retry: false,
    refetchOnWindowFocus: false
  });
};

const useGetUser = () => {
  const container = useContainer();
  return useQuery('user', () => new GetUser(container.get(HttpClient)).execute(), {
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 10
  });
};

export { BankAccountType, DocumentType, GetUserMock, VerificationStatus, useGetBusiness, useGetUser };
